import { asStringArray } from "core";
import type { AasApiAdapter, CreateApiKeyInput, UpdateApiKeyInput } from "../adapter";
import { requestJson } from "../http";
import type { ApiKeyRecord } from "../types";
import type { BackendApiKey, BackendApiKeyCreateResponse } from "./backendTypes";
import { asObject, toIsoDateTime, toNumber } from "./shared";

export function mapApiKey(row: BackendApiKey): ApiKeyRecord {
	const rateLimits = asObject(row.rateLimits);
	return {
		id: row.id,
		name: row.name,
		keyPrefix: row.keyPrefix,
		scopes: asStringArray(row.scopes),
		capabilities: asStringArray(row.capabilities),
		rateLimits: {
			tokensPerMinute: toNumber(rateLimits.tokens_per_minute),
			requestsPerMinute: toNumber(rateLimits.requests_per_minute),
			maxConcurrent: toNumber(rateLimits.max_concurrent),
			monthlyTokenBudget:
				rateLimits.monthly_token_budget === null || rateLimits.monthly_token_budget === undefined
					? undefined
					: toNumber(rateLimits.monthly_token_budget),
		},
		active: row.active,
		expiresAt: row.expiresAt ?? undefined,
		lastUsedAt: row.lastUsedAt ?? row.lastUsed ?? undefined,
	};
}

export const apiKeysApi: AasApiAdapter["apiKeys"] = {
	async list() {
		const keys = await requestJson<BackendApiKey[]>("/api/api-keys");
		return keys.map(mapApiKey);
	},
	async create(input: CreateApiKeyInput) {
		const created = await requestJson<BackendApiKeyCreateResponse>("/api/api-keys", {
			method: "POST",
			body: {
				name: input.name,
				scopes: input.scopes,
				capabilities: input.capabilities ?? [],
				rateLimits: {
					requests_per_minute: input.requestsPerMinute,
					max_concurrent: input.maxConcurrent,
					monthly_token_budget: input.monthlyTokenBudget ?? null,
				},
				expiresAt: toIsoDateTime(input.expiresAt, "end"),
			},
		});
		return {
			keyId: created.id,
			fullKey: created.key,
		};
	},
	async update(id: string, input: UpdateApiKeyInput) {
		const { rateLimits, expiresAt, ...rest } = input;
		const updated = await requestJson<BackendApiKey>(`/api/api-keys/${encodeURIComponent(id)}`, {
			method: "PATCH",
			body: {
				...rest,
				rateLimits: rateLimits && {
					tokens_per_minute: rateLimits.tokensPerMinute,
					requests_per_minute: rateLimits.requestsPerMinute,
					max_concurrent: rateLimits.maxConcurrent,
					monthly_token_budget: rateLimits.monthlyTokenBudget ?? null,
				},
				expiresAt: expiresAt === null ? null : toIsoDateTime(expiresAt, "end"),
			},
		});
		return mapApiKey(updated);
	},
	async revoke(id: string) {
		const updated = await requestJson<BackendApiKey>(`/api/api-keys/${encodeURIComponent(id)}`, {
			method: "PATCH",
			body: { active: false },
		});
		return mapApiKey(updated);
	},
	async delete(id: string) {
		await requestJson<void>(`/api/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" });
	},
};
